// ──────────────────────────────────────────────────────────────
// 행사 상세(description) 파싱 — 단일 진실 공급원
//
// 백엔드가 못 잡은 라벨(행사장소·입장료·사전예약·메인공연·상시체험 등)을
// description 원문에서 추출. "행사 모달 빈약"이 3회 반복된 곳이라 모듈 분리 + 회귀 테스트.
// (2026-06-18 모듈화 7단계)
// 공개 API: parseEventDesc, cleanEventTitle, fillBanner
// ──────────────────────────────────────────────────────────────

// (내부) 추출 대상 라벨 정규식 조각
const EV_LABEL_ALT = '행사\\s*기간|운영\\s*기간|행사\\s*일시|교육\\s*일시|교육\\s*일정|행사\\s*시간|관람\\s*시간|신청\\s*기간|접수\\s*기간|모집\\s*기간|행사\\s*장소|교육\\s*장소|공연\\s*장소|운영\\s*장소|행사\\s*위치|모집\\s*대상|참가\\s*대상|교육\\s*대상|입장\\s*료\\s*\\/?\\s*방법|입장\\s*료|관람료|이용료|참가비|수강료|교\\s*육\\s*비|사전\\s*예약|신청\\s*방법|참가\\s*신청|접수\\s*방법|주요\\s*내용|행사\\s*내용|공연\\s*내용|교육\\s*내용|메인\\s*공연|상세\\s*체험|상시\\s*체험|프로그램|안내\\s*사항|유의\\s*사항|문\\s*의처|문\\s*의|일\\s*시|기\\s*간|장\\s*소|위\\s*치|대\\s*상|예\\s*매|예\\s*약|비\\s*용|내\\s*용';

// (내부) 라벨 → 필드명 매핑
function evLabelField(lbl) {
  const k = lbl.replace(/\s+/g, '');
  if (/^(행사기간|운영기간|행사일시|교육일시|교육일정|행사시간|관람시간|일시|기간)$/.test(k)) return 'period';
  if (/^(행사장소|교육장소|공연장소|운영장소|행사위치|장소|위치)$/.test(k)) return 'place';
  if (/^(신청기간|접수기간|모집기간)$/.test(k)) return 'applyPeriod';
  if (/^(모집대상|참가대상|교육대상|대상)$/.test(k)) return 'target';
  if (/^(입장료\/방법|입장료|관람료|이용료|참가비|수강료|교육비|비용)$/.test(k)) return 'cost';
  if (/^(사전예약|예매|예약|신청방법|참가신청|접수방법)$/.test(k)) return 'applyMethod';
  if (/^(주요내용|행사내용|공연내용|교육내용|메인공연|상세체험|상시체험|프로그램|내용)$/.test(k)) return 'summary';
  if (/^(문의처|문의)$/.test(k)) return 'contact';
  if (/^(안내사항|유의사항)$/.test(k)) return 'note';
  return null;
}

// (내부) 이모지·특수문자·앞뒤 구분기호 정리
const evClean = (v) => String(v).replace(/[←-⇿⌀-➿⬀-⯿\u{1F000}-\u{1FAFF}️☎※👉]/gu, '').replace(/\s+/g, ' ').replace(/\s+[ㅇ○◇★☆■□‣▪◦▶◆]\s*$/, '').replace(/[·,/\s]+$/, '').replace(/^[\s\-‐–—·]+/, '').trim();

// (내부) 게시판 본문 잡음 경계 — 라벨 없는 prose에서 값이 다음 라벨까지 통째로 슬라이스되는 걸 차단.
// 담당부서·조회수·작성일·전화번호·이전글/다음글·날짜스탬프·"2)"·"가." 식 열거 앞에서 값을 끊는다. (2026-06-29 sanitizer)
const EV_NOISE_CUT = /(담당\s*부서|담당자|담당\s*팀|조회\s*수|조회\s*\d|작성일|등록일|작성자|게시일|전화\s*번호|첨부\s*파일|이전\s*글|다음\s*글|기타\s*정보|할인율|파트너|후원|주최|주관|\s목록\s|\s목록$|\d{4}-\d{1,2}-\d{1,2}|\s\d{1,2}\s*\)|\s[가-힣]\.\s|\s[ㅇ○◇★☆■□‣▪◦▶◆]\s)/;
// period·applyPeriod는 ISO 날짜(2026-07-01)가 값 그 자체 — 날짜스탬프 컷만 제외한 변형 (2026-07-03 회귀 수정)
const EV_NOISE_CUT_DATEOK = /(담당\s*부서|담당자|담당\s*팀|조회\s*수|조회\s*\d|작성일|등록일|작성자|게시일|전화\s*번호|첨부\s*파일|이전\s*글|다음\s*글|기타\s*정보|할인율|파트너|후원|주최|주관|\s목록\s|\s목록$|\s\d{1,2}\s*\)|\s[가-힣]\.\s|\s[ㅇ○◇★☆■□‣▪◦▶◆]\s)/;
const evCut = (v, field) => {
  const s = String(v);
  const mm = s.match(field === 'period' || field === 'applyPeriod' ? EV_NOISE_CUT_DATEOK : EV_NOISE_CUT);
  if (!mm) return s;
  return mm.index > 0 ? s.slice(0, mm.index).trim() : '';
};

// (내부) 단일 정제 게이트 — 정리 → 잡음경계 컷 → (contact는 전화번호까지만) → 타당성 검증. 실패 시 '' 반환.
// parseEventDesc(파싱값)·fillBanner(백엔드값) 양쪽이 같은 게이트를 쓰게 해 소스 불문 동일 품질 보장. (2026-06-29)
function sanitizeField(field, v) {
  let s = evCut(evClean(v), field);
  if (field === 'contact') { const pm = s.match(/^.*?\d{2,4}[-\s]?\d{3,4}[-\s]?\d{4}/); if (pm) s = evClean(pm[0]); }
  return evValid(field, s) ? s : '';
}

// (내부) 필드별 타당성 검증 — "그 칸에 맞는 값인가". 쓰레기 덩어리면 false → 칸 비움(빈칸 > 오정보).
function evValid(field, v) {
  if (!v) return false;
  const s = String(v).trim();
  if (s.replace(/\s/g, '').length < 2) return false;
  if (/담당\s*부서|담당자|조회\s*수|작성일|등록일|작성자|첨부\s*파일|이전\s*글|다음\s*글/.test(s)) return false;
  switch (field) {
    case 'place':       return s.length <= 40 && !/[<>[\]]/.test(s) && !/(20\d{2}[-.]\d|전화|02-|☎|모집|참여자|공고|사업\s|신청자격|교육비|다음글|이전글|개최|안내$)/.test(s); // 40자: 정당한 상세주소(도로명+층+홀) 보존 (2026-07-03 codex)
    case 'applyPeriod': return s.length <= 60 && /(20\d{2}|\d{1,2}\s*[.월]\s*\d{1,2}|상시|수시|연중|마감|예정|선착|접수\s*중)/.test(s);
    case 'applyMethod': return s.length <= 80 && /(신청|접수|방문|온라인|네이버|전화|링크|http|이메일|현장|선착순|추첨|폼|qr|큐알)/i.test(s);
    case 'period':      return s.length <= 70 && /(20\d{2}|\d{1,2}\s*[.:월~]\s*\d{1,2}|요일|매주|매월|상시|연중|시\s*~)/.test(s);
    case 'target':      return s.length <= 60 && !/(공고|다음글|이전글|사업\s)/.test(s) && !/\d{2,4}[-\s]?\d{3,4}[-\s]?\d{4}/.test(s); // 전화번호 오유입 차단 (2026-07-03 codex)
    case 'cost':        return s.length <= 40 && /(무료|유료|원|₩|\d)/.test(s); // 금액 형태만 (2026-07-03 codex)
    case 'contact':     return /\d{2,4}[-\s]?\d{3,4}[-\s]?\d{4}/.test(s) || (s.length <= 20 && /(과|팀|센터|실|국|부)$/.test(s));
    default:            return s.length <= 220; // summary 등
  }
}

// description 원문에서 라벨별 값 추출 (구조화 필드는 잡음 컷 + 타당성 검증 통과분만)
function parseEventDesc(desc) {
  const out = {};
  if (!desc) return out;
  const text = String(desc).replace(/\s+/g, ' ').trim();
  const re = new RegExp('(' + EV_LABEL_ALT + ')\\s*[:：]', 'g');
  const hits = []; let m;
  while ((m = re.exec(text))) hits.push({ label: m[1], valStart: m.index + m[0].length, start: m.index });
  const sumSeen = new Set();
  for (let i = 0; i < hits.length; i++) {
    const field = evLabelField(hits[i].label); if (!field) continue;
    const end = i + 1 < hits.length ? hits[i + 1].start : text.length;
    const rawVal = evClean(text.slice(hits[i].valStart, end));
    if (!rawVal || rawVal.length < 2) continue;
    if (field === 'summary') {
      const lk = hits[i].label.replace(/\s+/g, '');
      let val = rawVal;
      if (val.length > 140) val = val.slice(0, 140).trim() + '...';
      const piece = (lk === '내용' || lk === '주요내용') ? val : lk + ': ' + val;
      const key = piece.slice(0, 24);
      if (!sumSeen.has(key)) { sumSeen.add(key); out.summary = out.summary ? out.summary + ' / ' + piece : piece; }
      continue;
    }
    // 구조화 필드: 단일 게이트(잡음 컷 + 검증) 통과분만. 실패 시 칸 비움(덩어리 방지)
    const val = sanitizeField(field, rawVal);
    if (!out[field] && val) out[field] = val;
  }
  return out;
}

// 낚시성 제목 머리말 제거 (예: "[클릭하면 후회] ○○축제" → "○○축제")
function cleanEventTitle(t) {
  if (!t) return t;
  const s = String(t).trim().replace(/^\s*[([][^)\]]*(?:클릭|후회|진짜|대박|실화|소름|필독|주목|안하면|놓치면|꿀잼|레전드)[^)\]]*[)\]]\s*/, '').trim();
  return s || String(t).trim();
}

// 배너/행사 객체에 빈 필드를 description 파싱 결과로 채움 + 제목 정리
// 백엔드가 채워 보낸 필드도 타당성 검증 — 어느 소스든 칸에 안 맞는 덩어리는 폐기(빈칸 > 오정보).
// 서초처럼 깨끗한 필드는 검증 통과해 그대로 유지, 강남처럼 덩어리면 폐기 후 parsed로 대체. (2026-06-29)
function fillBanner(bd) {
  if (!bd) return bd;
  const parsed = parseEventDesc(bd.description || '');
  const merged = { ...bd };
  // 구조화 필드: 기존 값도 같은 게이트로 정제(서초처럼 깨끗하면 통과·끝잡음 제거, 강남처럼 덩어리면 ''),
  // 그래도 비면 description 파싱값(이미 정제됨)으로 대체
  ['period', 'place', 'target', 'cost', 'applyPeriod', 'applyMethod', 'contact'].forEach((f) => {
    const cur = merged[f] != null ? sanitizeField(f, String(merged[f])) : '';
    merged[f] = cur || parsed[f] || '';
  });
  // summary/note: 비어있을 때만 보강(본문성 텍스트라 강한 검증 안 함)
  ['summary', 'note'].forEach((f) => { if ((!merged[f] || String(merged[f]).trim() === '') && parsed[f]) merged[f] = parsed[f]; });
  // 문의에 전화번호 없으면 본문 "전화번호 0234235545" 병합 — 백엔드 sanitizeEventFields와 동일 규칙 (2026-07-04, 캐시된 옛 데이터 커버용 이중방어)
  if (!/\d{3,}/.test(merged.contact || '')) {
    const pm = String(bd.description || '').match(/전화\s*번호\s*[:：]?\s*(0\d{1,2}[-\s]?\d{3,4}[-\s]?\d{3,4})/);
    if (pm) {
      const d = pm[1].replace(/\D/g, '');
      const tel = d.length === 10 && d.startsWith('02') ? `02-${d.slice(2, 6)}-${d.slice(6)}` :
                  d.length === 9 && d.startsWith('02') ? `02-${d.slice(2, 5)}-${d.slice(5)}` :
                  d.length === 11 ? `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}` :
                  d.length === 10 ? `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}` : pm[1];
      merged.contact = ((merged.contact || '') + ' ' + tel).trim();
    }
  }
  if (merged.title) merged.title = cleanEventTitle(merged.title);
  return merged;
}

module.exports = { parseEventDesc, cleanEventTitle, fillBanner };
